import { useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { Icon } from './Icon';
import { useFieldWiring } from './useFieldWiring';

/** One choice in a {@link Combobox}. */
export interface ComboboxOption {
  /** The value handed back on selection. */
  readonly value: string;
  /** What the user reads and types against. */
  readonly label: string;
  /** A second line — an address, a part number, a role. */
  readonly description?: string;
}

export interface ComboboxProps {
  /** The full set of choices; filtering happens here, not in the caller. */
  readonly options: readonly ComboboxOption[];
  /** The selected value, or null. */
  readonly value: string | null;
  /** Called with the new value, or null when the selection is cleared. */
  readonly onChange: (value: string | null) => void;
  /** Hint text shown in the empty control. */
  readonly placeholder?: string;
  /** Shown when the query matches nothing. */
  readonly emptyMessage?: string;
  /** Accessible name, required when the combobox is used outside a FormField. */
  readonly 'aria-label'?: string;
}

/**
 * A single-select combobox: type to narrow, arrow keys to choose.
 *
 * Built to the ARIA 1.2 combobox pattern with `aria-activedescendant` rather
 * than roving focus, so focus never leaves the text field. That matters for a
 * dispatcher filling a form at speed: the caret stays put, typing keeps
 * working, and screen readers announce the highlighted option without the
 * focus-jumping chatter that a roving implementation produces.
 *
 * Filtering is substring, case-insensitive, over label *and* description — a
 * scheduler searching "5th Ave" should find the customer at that address.
 *
 * @param props see {@link ComboboxProps}
 * @returns the combobox
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  emptyMessage = 'No matches',
  'aria-label': ariaLabel,
}: ComboboxProps): React.JSX.Element {
  const { controlId, describedBy, invalid, required } = useFieldWiring();
  const listId = `${controlId}-listbox`;

  const selected = options.find((option) => option.value === value) ?? null;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') {
      return options;
    }
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.description ?? '').toLowerCase().includes(needle),
    );
  }, [options, query]);

  // The choices can shrink while one is highlighted (a search that comes back with fewer). An
  // index past the end would name an option that is not there, and Enter would select nothing.
  const active = Math.min(activeIndex, Math.max(0, matches.length - 1));

  const text = open ? query : (selected?.label ?? '');

  function openList(nextQuery: string): void {
    setQuery(nextQuery);
    setOpen(true);
    setActiveIndex(0);
  }

  function commit(index: number): void {
    const option = matches[index];
    if (option === undefined) {
      return;
    }
    onChange(option.value);
    setOpen(false);
    setQuery('');
  }

  function moveActive(step: 1 | -1): void {
    if (matches.length > 0) {
      setActiveIndex((active + step + matches.length) % matches.length);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          openList('');
          return;
        }
        moveActive(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) {
          openList('');
          return;
        }
        moveActive(-1);
        return;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        return;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(Math.max(0, matches.length - 1));
        }
        return;
      case 'Enter':
        if (open) {
          event.preventDefault();
          commit(active);
        }
        return;
      case 'Escape':
        if (open) {
          // Stops here rather than bubbling: Escape inside an open list closes
          // the list, and must not also close the dialog the list sits in.
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          setQuery('');
        }
        return;
      default:
    }
  }

  const activeOptionId =
    open && matches.length > 0 ? `${listId}-option-${String(active)}` : undefined;

  return (
    <div className="fo-combobox" data-invalid={invalid ? '' : undefined}>
      <div className="fo-combobox__control">
        <input
          ref={inputRef}
          id={controlId}
          className="fo-combobox__input"
          type="text"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-label={ariaLabel}
          required={required}
          placeholder={placeholder}
          value={text}
          onChange={(event) => {
            openList(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setOpen(false);
            setQuery('');
          }}
        />
        <button
          type="button"
          className="fo-combobox__toggle"
          tabIndex={-1}
          aria-hidden="true"
          onMouseDown={(event) => {
            // Keeps focus in the text field: a blur here would close the list
            // before the click that opened it could be read.
            event.preventDefault();
          }}
          onClick={() => {
            if (open) {
              setOpen(false);
            } else {
              openList('');
            }
            inputRef.current?.focus();
          }}
        >
          <Icon name="chevron-down" />
        </button>
      </div>

      {/* The result count is announced politely so a screen-reader user knows
          the list narrowed without having to arrow through it to find out. */}
      <span className="visually-hidden" role="status">
        {open ? `${String(matches.length)} matches available` : ''}
      </span>

      <ul
        className="fo-combobox__list"
        id={listId}
        role="listbox"
        hidden={!open}
        aria-label={ariaLabel ?? 'Suggestions'}
      >
        {matches.length === 0 ? (
          <li className="fo-combobox__empty" role="presentation">
            {emptyMessage}
          </li>
        ) : (
          matches.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-option-${String(index)}`}
              className="fo-combobox__option"
              role="option"
              aria-selected={option.value === value}
              data-active={index === active ? '' : undefined}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                commit(index);
              }}
              onMouseEnter={() => {
                setActiveIndex(index);
              }}
            >
              <span className="fo-combobox__option-label">{option.label}</span>
              {option.description === undefined ? null : (
                <span className="fo-combobox__option-description">{option.description}</span>
              )}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
